/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-29 07:46:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Icon, Button, Stack, PageActions } from '@shopify/polaris';
import { QuestionMarkMajor, PlayCircleMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { reduxForm, reset } from 'redux-form';

import { URL } from 'src/constants/links';
import DidYouKnow from 'src/components/views/analytics/did-you-know';
import MainMenu from 'src/components/containers/main-menu';
import Total from './total';
import AnalyticsTabs from 'src/components/views/analytics/analytics-tabs';
import { getShop } from 'src/helpers';
import {
  getStoreDate,
  buildInitialAnalyticsValue,
  buildAnalyticsValue
} from 'src/helpers/analytics-helpers';
import * as AnalyticsActions from 'src/stores/actions/analytics-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as FilterTreeActions from 'actions/filter-tree/filter-tree-actions';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import ThemeUpgradeFlow2Notification from 'src/components/containers/common/theme-upgrade-flow-2-notification';
import YoutubeModal from 'src/components/views/common/youtube-modal';

import { FEATURE_IDS } from 'src/constants/theme-upgrade';
import type { AnalyticsSettingsType } from 'src/models/analytics';

type Props = WithI18nProps;
type State = {
  filterDataLoaded: boolean,
  actionSurvey: boolean,
  selectedTab: number,
  youtubeModal: boolean
};
class Analytics extends Component<Props, State> {
  timeoutActionSurvey = undefined;

  state = {
    filterDataLoaded: false,
    actionSurvey: false,
    selectedTab: 0,

    youtubeModal: false
  };

  componentDidMount() {
    const { changeValue, getFilterData, getAllFilterOptions, getAnalyticsSettings, i18n } =
      this.props;
    // Get collection data
    getFilterData();

    // Get analytics settings
    getAnalyticsSettings();

    // Get all filter options for tenant
    getAllFilterOptions();

    // Check tabs
    const selectedTab = _.get(this.props.location, 'state.selectedTab', 0);
    this.setState({ selectedTab });

    // Initial from date, to date, compare
    const today = getStoreDate(new Date());
    let fromDate = today,
      toDate = today,
      compare = false,
      collection = {
        value: 'all',
        label: i18n.translate('AdminHTML.Analytics.Common.allCollections')
      };
    // Get data from local storage
    try {
      const fromDateLocalStorage = localStorage.getItem(`${getShop()}-analytic-fromDate`);
      const toDateLocalStorage = localStorage.getItem(`${getShop()}-analytic-toDate`);
      const compareLocalStorage = localStorage.getItem(`${getShop()}-analytic-compare`);
      const collectionLocalStorage = localStorage.getItem(`${getShop()}-analytic-collection`);

      fromDate = fromDateLocalStorage ? new Date(fromDateLocalStorage) : fromDate;
      toDate = toDateLocalStorage ? new Date(toDateLocalStorage) : fromDate;
      compare = compareLocalStorage === 'true';
      collection = collectionLocalStorage ? JSON.parse(collectionLocalStorage) : collection;
    } catch (error) {}

    changeValue('fromDate', fromDate);
    changeValue('toDate', toDate);
    changeValue('compare', compare);
    changeValue('collection', collection);

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.handleLeavePage);
  }
  componentWillReceiveProps(nextProps) {
    if (this.props.loading === false && nextProps.loading === true) {
      startSABLoading();
    }

    if (this.props.loading === true && nextProps.loading === false) {
      this.setState({ filterDataLoaded: true });
      stopSABLoading();
    }
  }

  componentWillUnmount() {
    this.handleLeavePage();

    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.handleLeavePage);
  }

  handleLeavePage = () => {
    // Insert data to local storage
    try {
      const { fromDate, toDate, compare = false, collection } = this.props;
      localStorage.setItem(`${getShop()}-analytic-fromDate`, fromDate.toISOString());
      localStorage.setItem(`${getShop()}-analytic-toDate`, toDate.toISOString());
      localStorage.setItem(`${getShop()}-analytic-compare`, compare.toString());
      localStorage.setItem(`${getShop()}-analytic-collection`, JSON.stringify(collection));
    } catch (error) {}
  };

  render() {
    const { filterDataLoaded, selectedTab, actionSurvey, youtubeModal } = this.state;
    const {
      pristine,
      submitting,
      loadingAnalyticsSettings,
      savingAnalyticsSettings,
      handleSubmit,
      selectedPlan,
      enableOrderRevenue,
      updatePermissionLink,
      i18n
    } = this.props;

    return (
      <div className="BC-Page BC-Page__Analytics" onScroll={this.handleScroll}>
        <MainMenu menuId="menu-analytics" />
        <Page
          title={i18n.translate('AdminHTML.Analytics.Analytics.analytics_PageTitle')}
          titleMetadata={
            <Stack spacing="tight">
              <span className="titleMetadata">
                <a href={URL['AnalyticsDoc']} target="_blank" rel="noopener noreferrer">
                  <Icon source={QuestionMarkMajor} color="base" />
                </a>
              </span>
              <span className="titleMetadata">
                <button onClick={() => this.toggleModal('youtubeModal')}>
                  <Icon source={PlayCircleMajor} color="base" />
                </button>
              </span>
            </Stack>
          }
          primaryAction={
            selectedTab === 2 && (
              <div className="BC-Page BC-Page__Button">
                <Stack>
                  {!pristine && (
                    <Button disabled={submitting} onClick={this.discardChange}>
                      {i18n.translate('AdminHTML.Common.Action.cancel')}
                    </Button>
                  )}
                  <Button
                    primary
                    onClick={handleSubmit(this.handleSave.bind(this))}
                    disabled={loadingAnalyticsSettings || pristine || submitting}
                    loading={savingAnalyticsSettings && !pristine}>
                    {i18n.translate('AdminHTML.Common.Action.save')}
                  </Button>
                </Stack>
              </div>
            )
          }>
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                <ThemeUpgradeFlow2Notification featureID={FEATURE_IDS.ANALYTICS} />

                {/* Did you know? */}
                <DidYouKnow />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            <Layout.Section>
              <Total />
            </Layout.Section>
            <Layout.Section>
              {filterDataLoaded ? (
                <AnalyticsTabs
                  selectedTab={selectedTab}
                  onChangeTab={this.onChangeTab}
                  performActionSurvey={this.performActionSurvey}
                  enableOrderRevenue={enableOrderRevenue}
                  updatePermissionLink={updatePermissionLink}
                />
              ) : (
                ''
              )}
            </Layout.Section>
          </Layout>
          {selectedTab === 2 && (
            <PageActions
              primaryAction={{
                content: i18n.translate('AdminHTML.Common.Action.save'),
                onAction: handleSubmit(this.handleSave.bind(this)),
                disabled: loadingAnalyticsSettings || pristine || submitting,
                loading: savingAnalyticsSettings && !pristine
              }}
              secondaryActions={
                !pristine
                  ? [
                      {
                        content: i18n.translate('AdminHTML.Common.Action.cancel'),
                        onAction: this.discardChange
                      }
                    ]
                  : []
              }
            />
          )}
        </Page>
        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded type="analytics" conditionShowStateChange={actionSurvey} />
        )}
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['AnalyticsTut']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate('AdminHTML.Analytics.Analytics.youtubeModal_Title')}
        />
      </div>
    );
  }

  // Run survey when user perform action: change tab, view detail, change date and scroll
  performActionSurvey = () => {
    const { actionSurvey } = this.state;

    clearTimeout(this.timeoutActionSurvey);

    this.timeoutActionSurvey = setTimeout(() => {
      this.setState({ actionSurvey: !actionSurvey });
    }, 30000);
  };

  // Handle scroll action
  handleScroll = e => {
    this.performActionSurvey();
  };

  onChangeTab = selectedTabIndex => {
    // Show action survey when change tab
    this.performActionSurvey();

    this.setState({ selectedTab: selectedTabIndex });
  };

  discardChange = () => {
    const { resetForm } = this.props;

    resetForm();
  };

  handleSave(data) {
    const { updateAnalyticsSettings } = this.props;
    // Update data before save
    const saveData = buildAnalyticsValue({ ...data });

    updateAnalyticsSettings(saveData);
  }

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get available collections
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getAllFilterOptions: () => dispatch(FilterTreeActions.getAllFilterOptions(ownProps.i18n)),
  resetForm: () => dispatch(reset('AnalyticsForm')),
  getAnalyticsSettings: () => dispatch(AnalyticsActions.getAnalyticsSettings(ownProps.i18n)),
  updateAnalyticsSettings: (analyticsSettings: AnalyticsSettingsType) =>
    dispatch(AnalyticsActions.updateAnalyticsSettings(analyticsSettings, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const analyticsSettings = _.get(state.analytics, 'analyticsSettings', {});

  return {
    loading: _.get(state.filterData, 'loading'),
    fromDate: _.get(state.analytics, 'fromDate'),
    toDate: _.get(state.analytics, 'toDate'),
    compare: _.get(state.analytics, 'compare', false),
    collection: _.get(state.analytics, 'collection', {
      value: 'all',
      label: props.i18n.translate('AdminHTML.Analytics.Common.allCollections')
    }),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    loadingAnalyticsSettings: _.get(state.analytics, 'loadingAnalyticsSettings', false),
    savingAnalyticsSettings: _.get(state.analytics, 'savingAnalyticsSettings', false),
    initialValues: buildInitialAnalyticsValue(analyticsSettings),
    enableOrderRevenue: _.get(analyticsSettings, 'enableOrderRevenue', false),
    updatePermissionLink: _.get(analyticsSettings, 'updatePermissionLink', '')
  };
};

const AnalyticsForm = reduxForm({
  form: 'AnalyticsForm', // a unique identifier for this form
  enableReinitialize: true
})(Analytics);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(AnalyticsForm));
